import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { format } from 'date-fns';
import { FiClock, FiFlag, FiInfo, FiUsers } from 'react-icons/fi';

import api from '../../../../services/api';
import { useAuth } from '../../../../hooks/auth';
import { useToast } from '../../../../hooks/useToast';

import {
  AssigneeChip,
  AssigneeList,
  EmptyMini,
  MetaRow,
  PriorityDot,
  Section,
  SidebarWrapper,
  Timeline,
  TimelineItem,
} from './styles';

interface IUser {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
}

interface IMessage {
  id: string;
  key_from_server: 'income' | 'outgoing';
  message_type: 'text' | 'audio' | 'document' | 'image';
  data: string;
  media_url: string;
  media_server_url: string;
  media_mime_type: string;
  media_size: number;
  media_server_size: number;
  latitude: number;
  longitude: number;
  created_at: Date;
}

interface IComplaint {
  id: string;
  protocol: string;
  status: number;
  priority: number;
  company_id: string;
  company?: { nome_fantasia?: string };
  city?: { name?: string };
  messages: IMessage[];
  assignedUsers: IUser[];
  is_archived: boolean;
  created_at?: Date;
}

interface ILog {
  id: string;
  type: string;
  module: string;
  message: string;
  feedback: string;
  created_at: Date;
}

interface ISelectOption {
  value: string;
  label: string;
}

interface IProps {
  complaint: IComplaint | undefined;
  logs: ILog[];
}

const PRIORITY_OPTIONS: Array<{
  value: string;
  label: string;
  tone: 'neutral' | 'info' | 'warning' | 'danger';
}> = [
  { value: '0', label: 'Não urgente', tone: 'neutral' },
  { value: '1', label: 'Pouco urgente', tone: 'neutral' },
  { value: '2', label: 'Normal', tone: 'info' },
  { value: '3', label: 'Urgente', tone: 'warning' },
  { value: '4', label: 'Muito urgente', tone: 'danger' },
];

const getInitials = (first: string, last: string) =>
  `${(first || '?')[0]}${(last || '')[0]}`.toUpperCase();

const LeftSidebar: React.FC<IProps> = ({ complaint, logs }) => {
  const { addToast } = useToast();
  const { user: authUser } = useAuth();

  const [users, setUsers] = useState<ISelectOption[]>([]);

  const handleAssigneeUser = useCallback(
    async (assignedUsers: ISelectOption[] | null) => {
      try {
        const ids = (assignedUsers || []).map(u => u.value);
        await api.put(`/complaints/${complaint?.id}`, { assignedUsers: ids });
        addToast({ title: 'Sucesso', body: 'Responsáveis atualizados.' });
      } catch {
        addToast({
          title: 'Erro',
          body: 'Falha ao atualizar responsáveis.',
        });
      }
    },
    [addToast, complaint],
  );

  const handleUpdatePriority = useCallback(
    async (priority: ISelectOption | null) => {
      if (!priority) return;
      try {
        await api.put(`/complaints/${complaint?.id}`, {
          priority: priority.value,
        });
        addToast({ title: 'Sucesso', body: 'Prioridade atualizada.' });
      } catch {
        addToast({ title: 'Erro', body: 'Falha ao atualizar a prioridade.' });
      }
    },
    [addToast, complaint],
  );

  useEffect(() => {
    const load = async () => {
      try {
        const response = await api.get<IUser[]>('/users');
        setUsers(
          response.data.map(u => ({
            value: u.id,
            label: `${u.first_name} ${u.last_name}`,
          })),
        );
      } catch {
        /* silent */
      }
    };
    load();
  }, []);

  const priorityValue = useMemo(() => {
    if (!complaint) return undefined;
    return PRIORITY_OPTIONS.find(p => p.value === String(complaint.priority));
  }, [complaint]);

  const assignedValue = useMemo(() => {
    if (!complaint) return [];
    return complaint.assignedUsers.map(u => ({
      value: u.id,
      label: `${u.first_name} ${u.last_name}`,
    }));
  }, [complaint]);

  return (
    <SidebarWrapper>
      <Section>
        <h4>
          <FiInfo size={12} />
          Detalhes
        </h4>
        {complaint?.company?.nome_fantasia && (
          <MetaRow>
            <span className="label">Empresa</span>
            <span className="value">{complaint.company.nome_fantasia}</span>
          </MetaRow>
        )}
        {complaint?.city?.name && (
          <MetaRow>
            <span className="label">Cidade</span>
            <span className="value">{complaint.city.name}</span>
          </MetaRow>
        )}
        {complaint?.created_at && (
          <MetaRow>
            <span className="label">Aberta em</span>
            <span className="value">
              {format(new Date(complaint.created_at), 'dd/MM/yyyy HH:mm')}
            </span>
          </MetaRow>
        )}
        <MetaRow>
          <span className="label">Mensagens</span>
          <span className="value">{complaint?.messages?.length || 0}</span>
        </MetaRow>
      </Section>

      <Section>
        <h4>
          <FiUsers size={12} />
          Responsáveis
        </h4>
        <Select
          isMulti
          options={users}
          placeholder="Selecione os responsáveis"
          classNamePrefix="react-select"
          value={assignedValue}
          onChange={value => handleAssigneeUser(value as ISelectOption[])}
        />
        {assignedValue.length > 0 && (
          <AssigneeList>
            {complaint?.assignedUsers.map(u => (
              <AssigneeChip key={u.id}>
                <span className="avatar">
                  {getInitials(u.first_name, u.last_name)}
                </span>
                {u.first_name}
                {' '}
                {u.last_name}
              </AssigneeChip>
            ))}
          </AssigneeList>
        )}
      </Section>

      <Section>
        <h4>
          <FiFlag size={12} />
          Prioridade
        </h4>
        <Select
          options={PRIORITY_OPTIONS}
          classNamePrefix="react-select"
          value={priorityValue || null}
          placeholder="Selecione a prioridade"
          onChange={value => handleUpdatePriority(value as ISelectOption)}
          formatOptionLabel={(option, { context }) => {
            const { tone } = option as (typeof PRIORITY_OPTIONS)[number];
            return (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: context === 'menu' ? 13 : 13,
                }}
              >
                <PriorityDot $tone={tone} />
                {option.label}
              </span>
            );
          }}
        />
      </Section>

      <Section>
        <h4>
          <FiClock size={12} />
          Histórico
        </h4>
        {logs.length === 0 ? (
          <EmptyMini>Nenhum evento registrado.</EmptyMini>
        ) : (
          <Timeline>
            {logs.map(log => (
              <TimelineItem key={log.id}>
                <div className="message">{log.message}</div>
                {log.type === 'feedback' &&
                  authUser.role === 'admin' &&
                  log.feedback && (
                    <div className="feedback">
                      <strong>Feedback:</strong>
                      {' '}
                      {log.feedback}
                    </div>
                  )}
                <div className="timestamp">
                  {format(new Date(log.created_at), "dd/MM/yyyy 'às' HH:mm")}
                </div>
              </TimelineItem>
            ))}
          </Timeline>
        )}
      </Section>
    </SidebarWrapper>
  );
};

export default LeftSidebar;
